import Fuse from 'fuse.js';

// Configure Fuse.js options for better fuzzy searching
const fuseOptions = {
  keys: ['name', 'city', 'state', 'address', 'affiliate'],
  threshold: 0.3,
  distance: 100,
  minMatchCharLength: 2
};

// Parse location queries like "near city, state" or "in state"
export function parseLocationQuery(query) {
  const locationRegex = /(?:near|in)\s+([^,]+)(?:\s*,\s*(\w+))?/i;
  const match = query.match(locationRegex);
  
  if (match) {
    return {
      type: 'location',
      city: match[1]?.trim().toLowerCase(),
      state: match[2]?.trim().toLowerCase(),
      original: match[0]
    };
  }
  return null;
}

// Parse capacity queries like "capacity > 1000" or "less than 500 capacity"
export function parseCapacityQuery(query) {
  const capacityRegex = /(?:capacity\s*([<>])\s*(\d+))|(?:(less|more)\s+than\s+(\d+)\s+capacity)/i;
  const match = query.match(capacityRegex);
  
  if (match) {
    const operator = match[1] || (match[3] === 'less' ? '<' : '>');
    const value = parseInt(match[2] || match[4]);
    return {
      type: 'capacity',
      operator,
      value,
      original: match[0]
    };
  }
  return null;
}

export function parseNaturalLanguageQuery(query) {
  const locationMatch = parseLocationQuery(query);
  const capacityMatch = parseCapacityQuery(query);
  
  return {
    location: locationMatch,
    capacity: capacityMatch,
    remaining: query
      .replace(locationMatch?.original || '', '')
      .replace(capacityMatch?.original || '', '')
      .trim()
  };
}

export function performFuzzySearch(venues, query) {
  try {
    const parsedQuery = parseNaturalLanguageQuery(query);
    let filtered = [...venues];
    
    // Handle location filtering
    if (parsedQuery.location) {
      filtered = filtered.filter(venue => {
        const venueCity = venue.city?.toLowerCase();
        const venueState = venue.state?.toLowerCase();
        
        if (parsedQuery.location.state) {
          return venueCity?.includes(parsedQuery.location.city) && 
                 venueState?.includes(parsedQuery.location.state);
        }
        return venueCity?.includes(parsedQuery.location.city);
      });
    }
    
    // Handle capacity filtering
    if (parsedQuery.capacity) {
      filtered = filtered.filter(venue => {
        const capacity = parseInt(venue.capacity?.toString().replace(/[^0-9]/g, '') || '0');
        return parsedQuery.capacity.operator === '>' ? 
          capacity > parsedQuery.capacity.value :
          capacity < parsedQuery.capacity.value;
      });
    }
    
    // If there's remaining text, use fuzzy search
    if (parsedQuery.remaining) {
      const searchLower = parsedQuery.remaining.toLowerCase();
      
      // Check for exact matches first (prioritize these at the top)
      const exactMatches = filtered.filter(venue => 
        venue.name.toLowerCase() === searchLower ||
        venue.city.toLowerCase() === searchLower ||
        venue.state.toLowerCase() === searchLower
      );
      
      // Then check for venues whose names start with the search term
      const startsWithMatches = filtered.filter(venue => 
        venue.name.toLowerCase().startsWith(searchLower) &&
        venue.name.toLowerCase() !== searchLower // exclude exact matches
      );
      
      // Use Fuse.js for the rest (fuzzy matching)
      const fuse = new Fuse(filtered.filter(venue => 
        !exactMatches.includes(venue) && 
        !startsWithMatches.includes(venue)
      ), fuseOptions);
      
      const fuzzyMatches = fuse.search(parsedQuery.remaining).map(result => result.item);
      
      // Combine all results, prioritizing exact matches, then startsWith matches, then fuzzy matches
      return [...exactMatches, ...startsWithMatches, ...fuzzyMatches];
    }
    
    return filtered;
  } catch (error) {
    console.error('Search error:', error);
    return venues; // Return all venues if search fails
  }
} 